import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ideasApi } from '../lib/api';
import IdeaCard from '../components/vault/IdeaCard';
import IdeaDetail from '../components/vault/IdeaDetail';
import NewIdeaModal from '../components/vault/NewIdeaModal';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'concept', label: 'Concept' },
  { value: 'prototype', label: 'Prototype' },
  { value: 'pitched', label: 'Pitched' },
  { value: 'licensed', label: 'Licensed' },
  { value: 'sleeping', label: 'Sleeping' },
  { value: 'archived', label: 'Archived' },
];

export default function IdeaVault() {
  const { role } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // [EVENT-MODE] Only admins can create ideas via vault; inventors must use event submission
  const canCreateIdea = role === 'admin';

  const fetchIdeas = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;
      const data = await ideasApi.list(params);
      setIdeas(data.ideas || []);
    } catch (err) {
      console.error('Failed to fetch ideas:', err);
    }
    setLoading(false);
  }, [filterStatus, searchQuery]);

  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  const handleCreateIdea = async (ideaData, files) => {
    const data = await ideasApi.create(ideaData);
    // Upload files if any
    if (files?.length > 0 && data.idea?.id) {
      for (const file of files) {
        await ideasApi.uploadAsset(data.idea.id, file);
      }
    }
    await fetchIdeas();
  };

  const handleUpdateIdea = async (id, updates) => {
    await ideasApi.update(id, updates);
    await fetchIdeas();
    // Refresh selected idea
    const fresh = await ideasApi.get(id);
    setSelectedIdea(fresh.idea);
  };

  const handleDeleteIdea = async (id) => {
    await ideasApi.delete(id);
    setSelectedIdea(null);
    await fetchIdeas();
  };

  const handleSelectIdea = async (idea) => {
    const fresh = await ideasApi.get(idea.id);
    setSelectedIdea(fresh.idea);
  };

  // Detail view
  if (selectedIdea) {
    return (
      <IdeaDetail
        idea={selectedIdea}
        onBack={() => setSelectedIdea(null)}
        onUpdate={handleUpdateIdea}
        onDelete={handleDeleteIdea}
      />
    );
  }

  // Grid view
  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-vault-navy">Idea Vault</h1>
          <p className="text-gray-500 text-sm mt-1">{ideas.length} idea{ideas.length !== 1 ? 's' : ''} in your vault</p>
        </div>
        {canCreateIdea && (
          <button
            onClick={() => setShowNewModal(true)}
            className="px-4 py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <span className="text-lg">+</span> New Idea
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search ideas..."
            className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold focus:border-transparent text-sm"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setFilterStatus(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === f.value
                  ? 'bg-vault-gold text-white'
                  : 'bg-white border border-vault-border text-gray-600 hover:border-vault-gold/50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading ideas...</div>
      ) : ideas.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4 opacity-30">&#128161;</div>
          <h3 className="text-lg font-semibold text-vault-navy mb-2">Your vault is empty</h3>
          <p className="text-gray-500 text-sm mb-4">
            {canCreateIdea
              ? 'Start adding your toy and game ideas. AI will profile each one automatically.'
              : 'Ideas are submitted through the official event submission form.'}
          </p>
          {canCreateIdea && (
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors"
            >
              Add Your First Idea
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {ideas.map(idea => (
            <IdeaCard key={idea.id} idea={idea} onClick={handleSelectIdea} />
          ))}
        </div>
      )}

      {/* New Idea Modal */}
      {showNewModal && (
        <NewIdeaModal
          onClose={() => setShowNewModal(false)}
          onSave={handleCreateIdea}
        />
      )}
    </div>
  );
}
